import React, { JSX, ReactNode } from 'react'

import parse, { DOMNode, Element, domToReact } from 'html-react-parser'
import showdown from 'showdown'

import {
  Recommendation,
  RecommendationKeyEnum,
  RecommendationRegistry,
} from './recommendationTypes'
import SidebarLink from '../components/SidebarLink'
import {
  ConcernTag,
  RecommendationKey,
  RecommendationTaskType,
} from '../utils/enums'

const converter = new showdown.Converter()

function parseReplaceSidebarLink(
  domNode: Element,
  metadata?: RecommendationDisplayWrapperMetadata,
): JSX.Element | null {
  if (domNode.name === 'a' && domNode.children) {
    let href: string = domNode.attribs.href
    if (metadata?.googleAccountIndex) {
      const url = new URL(href)
      url.searchParams.set('authuser', metadata.googleAccountIndex.toString())
      href = url.toString()
    }
    return (
      <SidebarLink href={href}>
        {domToReact(domNode.children as DOMNode[])}
      </SidebarLink>
    )
  }
  return null
}

function convertYamlMarkdownToReact(
  rawMarkdownText: string | undefined | null,
  metadata?: RecommendationDisplayWrapperMetadata,
): ReactNode {
  if (!rawMarkdownText) {
    return null
  }

  return parse(converter.makeHtml(rawMarkdownText), {
    replace: (domNode: Element) => parseReplaceSidebarLink(domNode, metadata),
  })
}

export type Step = { id: number; description: ReactNode }
export type Setting = { key: string; description: ReactNode }

export default class RecommendationDisplayWrapper {
  recommendation: Recommendation

  topConcernTag: ConcernTag

  metadata?: RecommendationDisplayWrapperMetadata

  constructor(
    recommendation: Recommendation,
    topConcernTag: ConcernTag,
    metadata?: RecommendationDisplayWrapperMetadata,
  ) {
    this.recommendation = recommendation
    this.topConcernTag = topConcernTag
    this.metadata = metadata
  }

  get accountTypes(): string | string[] | undefined {
    return this.recommendation.accountTypes
  }

  get bodyTitle(): ReactNode {
    return convertYamlMarkdownToReact(this.recommendation.bodyTitle)
  }

  get canUndo(): boolean {
    return this.recommendation.canUndo
  }

  get concernTags(): ConcernTag[] {
    return this.recommendation.concernTags as ConcernTag[]
  }

  get confirmTaskButtonText(): string | null {
    return this.recommendation.confirmTaskButtonText || null
  }

  get continuingTaskButtonText(): string | null {
    return this.recommendation.continuingTaskButtonText || null
  }

  get customActionDescription(): string | undefined {
    return this.recommendation.customActionDescription
  }

  get explanationText(): ReactNode {
    if (this.recommendation.explanationTextByConcernTag) {
      let markdownText =
        this.recommendation.explanationTextByConcernTag[this.topConcernTag]
      if (!markdownText) {
        markdownText =
          this.recommendation.explanationTextByConcernTag[
            this.recommendation.concernTags[0]
          ]
      }

      return convertYamlMarkdownToReact(markdownText)
    }
    return convertYamlMarkdownToReact(this.recommendation.explanationTextOld)
  }

  get hasAutomatedGather(): boolean {
    return this.recommendation.hasAutomatedGather || false
  }

  get isAutomated(): boolean {
    return this.recommendation.isAutomated
  }

  get isBonus(): boolean {
    return this.recommendation.bonus || false
  }

  get isPhoto(): boolean {
    return this.recommendation.isPhoto || false
  }

  get isTask(): boolean {
    return this.recommendation.isTask || false
  }

  get key(): RecommendationKeyEnum {
    return this.recommendation.key
  }

  get recommendationCardType(): string {
    return this.recommendation.recommendationCardType
  }

  get recommendationTitle(): string {
    return this.recommendation.recommendationTitle
  }

  get deeplinkShareUrl(): string | undefined {
    return this.recommendation.deeplinkShareUrl
  }

  get settings(): Setting[] {
    if (!this.recommendation.settings) {
      return []
    }

    return this.recommendation.settings.map((setting) => ({
      key: setting.key,
      // Links are parsed and replaced with SettingsLink components, so we pass
      // metadata in case this is a Google recommendation
      description: convertYamlMarkdownToReact(
        setting.description,
        this.metadata,
      ),
    }))
  }

  get steps(): Step[] {
    if (!this.recommendation.steps) {
      return []
    }

    return this.recommendation.steps.map((step, i) => ({
      id: i,
      // Links are parsed and replaced with SettingsLink components, so we pass
      // metadata in case this is a Google recommendation
      description: convertYamlMarkdownToReact(step.description, this.metadata),
    }))
  }

  get taskButtonText(): string | null {
    return this.recommendation.taskButtonText || null
  }

  get taskContext(): string[] {
    return this.recommendation.taskContext || []
  }

  get taskTitle(): string | null {
    return this.recommendation.taskTitle || null
  }

  get taskType(): RecommendationTaskType {
    return this.recommendation.taskType
  }

  get taskUpdatedText(): string | null {
    return this.recommendation.taskUpdatedText || null
  }

  get taskWarningText(): string | null {
    return this.recommendation.taskWarningText || null
  }

  get shortTitle(): string | null {
    return this.recommendation.shortTitle || null
  }
}

export interface RecommendationDisplayWrapperMetadata {
  // Used for Google recommendations so embedded links use the correct account index
  googleAccountIndex?: number
}

export function wrapRecommendationRegistry(
  registry: RecommendationRegistry,
  metadata?: RecommendationDisplayWrapperMetadata,
): Partial<Record<RecommendationKey, RecommendationDisplayWrapper>> {
  return Object.fromEntries(
    Object.entries(registry).map(([key, spec]) => [
      key,
      // @TODO: Remove the second parameter here, as we're not using user
      // concerns anymore
      new RecommendationDisplayWrapper(spec, spec.concernTags[0], metadata),
    ]),
  )
}
